import { Monitor, Moon, Sun } from 'lucide-react'
import type { ThemeSetting } from '@insightai/tokens'
import { cn } from '../lib/cn'
import { focusRing } from '../lib/focus'
import { useTheme } from './ThemeProvider'

const OPTIONS: { value: ThemeSetting; label: string; Icon: typeof Sun }[] = [
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'system', label: 'System', Icon: Monitor },
]

/**
 * Three-state theme control. A two-state toggle cannot express "follow the OS",
 * which is the only setting that stays correct when the user changes their
 * system preference.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme()
  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn('inline-flex rounded-md border border-border-subtle bg-surface-inset p-0.5', className)}
    >
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={theme === value}
          onClick={() => setTheme(value)}
          title={label}
          className={cn(
            'flex size-7 items-center justify-center rounded-sm text-text-secondary transition-colors duration-fast',
            'hover:text-text-primary',
            theme === value && 'bg-surface-raised text-text-primary shadow-sm',
            focusRing,
          )}
        >
          <Icon aria-hidden className="size-3.5" />
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  )
}
